import React, { useState, useEffect } from 'react';
import AppConfig from 'constants/config';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import CashbackIcon from './CashbackIcon';
import './index.css';

/*
The store-wide (seller-tier) offer this seller is currently running, shown on the view-seller page
below Sales Statistic. A seller runs at most one seller-tier campaign at a time - either a cashback
or a free gift - both earned per order once the order clears the campaign's minimum order value.

Data comes from `seller/get_buyer_seller_stats` (detail.seller_offer), which is null when nothing is
live. That endpoint is the order-free variant of `get_stats` - the buyer side needs no order counts.
*/

const inr = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

// One-line description of the cashback reward, matching how the seller set it up.
const cashbackReward = (o) => {
  if (o.cashback_type === 'flat') return `Flat ₹${inr(o.cashback_value)} cashback`;
  if (o.cashback_type === 'minimum') {
    // The ceiling sits beside the guarantee because the two are the ends of the same band: quoting
    // only "at least ₹150" on a percentage reads as unbounded on a large order, when the payout is
    // in fact capped at cashback_max. Stating both is what the buyer is actually promised.
    //
    // Dropped when the campaign carries no ceiling (cashback_max 0) - one approved before the field
    // existed - rather than printing "up to ₹0", which would read as a cashback of nothing.
    const band = Number(o.cashback_max) > 0
      ? `at least ₹${inr(o.cashback_floor)}, up to ₹${inr(o.cashback_max)}`
      : `at least ₹${inr(o.cashback_floor)}`;
    return `${o.cashback_percent}% cashback (${band})`;
  }
  // proportionate - the ceiling is named for the same reason it is on the floored type above: a bare
  // "2% cashback" reads as unbounded on a large order, when the payout is in fact capped at
  // cashback_max. Same 0 guard too - a campaign approved before the field existed carries no ceiling,
  // and "up to ₹0" would read as a cashback of nothing.
  return Number(o.cashback_max) > 0
    ? `${o.cashback_percent}% cashback (up to ₹${inr(o.cashback_max)})`
    : `${o.cashback_percent}% cashback`;
};

const SellerOffer = (props) => {
  const params = useParams();
  const [offer, setOffer] = useState(null);
  const [loader, setLoader] = useState(true);

  useEffect(() => {
    let active = true;
    axios({
      method: 'post',
      url: `${AppConfig.baseUrl}seller/get_buyer_seller_stats`,
      data: { seller_id: params.id }
    })
      .then((results) => {
        if (!active) return;
        setOffer(results.data && results.data.detail ? results.data.detail.seller_offer : null);
        setLoader(false);
      })
      .catch(() => { if (active) setLoader(false); });
    return () => { active = false; };
  }, [params.id]);

  if (loader) {
    return (
      <div className="loader-view">
        <CircularProgress />
      </div>
    );
  }

  if (!offer) {
    return <p className="mb-0 text-muted">No store-wide cashback or gift running right now.</p>;
  }

  const isGift = offer.type === 'gift';
  // Same thumb treatment as the Sales Statistic cards (InfoCard): saturated fill + matching glow.
  const accent = isGift ? '#d81b60' : '#0a8f2c';
  const glow = isGift
    ? '0 6px 20px 0 rgba(216, 27, 96, 0.19),0 8px 17px 0 rgba(216, 27, 96, 0.20)'
    : '0 6px 20px 0 rgba(10, 143, 44, 0.19),0 8px 17px 0 rgba(10, 143, 44, 0.20)';

  return (
    <div className="sellerOfferCard">
      <div
        className="sellerOfferCard-thumb"
        style={{ backgroundColor: accent, boxShadow: glow }}
      >
        {isGift
          ? <i className="zmdi zmdi-card-giftcard" />
          : <CashbackIcon title="Cashback" />}
      </div>
      <div className="br-break">
        <h4 className="mb-0">
          <strong>
            {isGift
              ? `${(offer.gift && offer.gift.description) || 'Free gift'}${offer.gift && offer.gift.brand ? ` - ${offer.gift.brand}` : ''}`
              : cashbackReward(offer)}
          </strong>
        </h4>
        <p className="mb-0">On every order above ₹{inr(offer.min_order_value)}</p>
        <p className="mb-0 text-muted" style={{ fontSize: 12 }}>
          Applied automatically at checkout.
        </p>
      </div>
    </div>
  );
};

export default SellerOffer;
